import * as THREE from 'three';
import type { MaterialLibrary } from '../assets/MaterialLibrary';

type Particle = {
  mesh: THREE.Mesh;
  velocity: THREE.Vector3;
  life: number;
  maxLife: number;
};

export class VfxSystem {
  private readonly particles: Particle[] = [];
  private readonly group: THREE.Group;
  private readonly flashPool: THREE.Mesh[] = [];

  constructor(
    scene: THREE.Scene,
    private readonly materials: MaterialLibrary,
  ) {
    this.group = new THREE.Group();
    this.group.name = 'vfx';
    scene.add(this.group);
  }

  spawnExplosion(position: THREE.Vector3, scale = 1, isSplash = false): void {
    const count = isSplash ? 14 : 10;
    for (let i = 0; i < count; i++) {
      const size = (0.06 + Math.random() * 0.1) * scale;
      const mesh = new THREE.Mesh(new THREE.SphereGeometry(size, 4, 4), this.materials.ember);
      mesh.position.copy(position);
      mesh.position.x += (Math.random() - 0.5) * 0.2 * scale;
      mesh.position.y += (Math.random() - 0.5) * 0.2 * scale;
      mesh.position.z += (Math.random() - 0.5) * 0.2 * scale;
      this.group.add(mesh);

      const velocity = new THREE.Vector3(
        (Math.random() - 0.5) * 2,
        Math.random() * 1.5 + 0.5,
        (Math.random() - 0.5) * 2,
      ).multiplyScalar(scale * (isSplash ? 1.6 : 1.2));

      this.particles.push({ mesh, velocity, life: 0.6 + Math.random() * 0.4, maxLife: 1 });
    }

    if (isSplash) {
      const smoke = new THREE.Mesh(new THREE.SphereGeometry(0.35 * scale, 8, 8), this.materials.smoke);
      smoke.position.copy(position);
      this.group.add(smoke);
      this.particles.push({
        mesh: smoke,
        velocity: new THREE.Vector3(0, 0.3 * scale, 0),
        life: 1.2,
        maxLife: 1.5,
      });
    }
  }

  spawnMuzzleFlash(position: THREE.Vector3): void {
    const flash = this.flashPool.pop() ?? new THREE.Mesh(new THREE.SphereGeometry(0.12, 6, 6), this.materials.ember);
    flash.position.copy(position);
    flash.scale.setScalar(1);
    flash.visible = true;
    this.group.add(flash);

    this.particles.push({
      mesh: flash,
      velocity: new THREE.Vector3(0, 0, 0),
      life: 0.06,
      maxLife: 0.08,
    });
  }

  spawnEnemyMuzzleFlash(position: THREE.Vector3): void {
    const flash = new THREE.Mesh(new THREE.SphereGeometry(0.2, 8, 8), this.materials.enemyFire);
    flash.position.copy(position);
    this.group.add(flash);

    this.particles.push({
      mesh: flash,
      velocity: new THREE.Vector3(0, 0, 0),
      life: 0.08,
      maxLife: 0.1,
    });
  }

  spawnTracer(start: THREE.Vector3, end: THREE.Vector3): void {
    const length = start.distanceTo(end);
    if (length < 0.01) return;

    const geometry = new THREE.CylinderGeometry(0.025, 0.025, length, 6, 1);
    geometry.rotateX(-Math.PI / 2);

    const tracer = new THREE.Mesh(geometry, this.materials.enemyFire);
    tracer.position.copy(start).add(end).multiplyScalar(0.5);
    tracer.lookAt(end);
    this.group.add(tracer);

    this.particles.push({
      mesh: tracer,
      velocity: new THREE.Vector3(0, 0, 0),
      life: 0.08,
      maxLife: 0.1,
    });
  }

  update(delta: number): void {
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.life -= delta;

      p.mesh.position.addScaledVector(p.velocity, delta);
      p.velocity.y -= 2.5 * delta;
      const scale = Math.max(0.01, p.life / p.maxLife);
      p.mesh.scale.setScalar(scale);

      if (p.life <= 0) {
        p.mesh.visible = false;
        this.group.remove(p.mesh);
        this.particles.splice(i, 1);

        if (p.mesh.geometry instanceof THREE.SphereGeometry && p.mesh.geometry.parameters.radius === 0.12) {
          this.flashPool.push(p.mesh);
        } else {
          p.mesh.geometry.dispose();
        }
      }
    }
  }

  clear(): void {
    for (const p of this.particles) {
      this.group.remove(p.mesh);
      p.mesh.geometry.dispose();
    }
    this.particles.length = 0;

    for (const flash of this.flashPool) {
      flash.geometry.dispose();
    }
    this.flashPool.length = 0;
  }
}
